<template>
  <div class="info-card" v-if="isAdmin || isTeacher || isAssistant">
    <div class="card-header">
      <div class="card-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      </div>
      <h3>Diák pénzügyek és adatok</h3>
    </div>

    <div class="info-list">
      <!-- Birth date (Year / Month) -->
      <div class="info-item">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          Születési idő
        </div>
        <div class="info-value finance-row">
          <input type="number" class="modern-input small" v-model.number="localBirthYear" placeholder="Év" min="1900" :max="new Date().getFullYear()" />
          <input type="number" class="modern-input small" v-model.number="localBirthMonth" placeholder="Hónap" min="1" max="12" />
          <button class="modern-btn modern-btn--primary btn-sm" @click="$emit('save-birth', { year: localBirthYear, month: localBirthMonth })">Mentés</button>
        </div>
      </div>

      <!-- Prepaid balance -->
      <div class="info-item payment-info-row">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <line x1="2" y1="10" x2="22" y2="10"/>
          </svg>
          Egyenleg
        </div>
        <div class="info-value payment-info-value">
          <div class="current-balance-display">
            <span class="fs-5 fw-bold" :class="balance < 0 ? 'text-danger' : 'text-success'">
              {{ formatHuf(balance) }}
            </span>
            <small v-if="monthlyFee > 0" class="text-muted">
              (Havi díj: {{ formatHuf(monthlyFee) }})
            </small>
          </div>
          <div class="payment-buttons-group">
            <button 
              v-if="!monthlyFeePaid"
              class="modern-btn modern-btn--success btn-sm" 
              @click="$emit('top-up-monthly')"
              :title="monthlyFee > 0 ? 'Havi csoportdíj befizetése (' + formatHuf(monthlyFee) + ')' : 'Havi csoportdíj befizetése'"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 5v14m7-7H5"/>
              </svg>
              {{ monthlyFee > 0 ? 'Havi díj (' + formatHuf(monthlyFee) + ')' : 'Havi díj feltöltés' }}
            </button>
            <button 
              v-else
              disabled
              class="modern-btn modern-btn--disabled btn-sm" 
              style="background-color: var(--color-background-soft); border-color: var(--color-border); color: var(--color-text-muted); cursor: not-allowed;"
              :title="'Ebben a hónapban (' + currentMonth + ') a havi csoportdíj már rendezve van / le van zárva'"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Havi díj rendezve ({{ formatHuf(monthlyFee) }})
            </button>
            <button 
              class="modern-btn modern-btn--primary btn-sm" 
              @click="$emit('open-custom-topup')"
              title="Egyedi összeg feltöltése"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v8m-4-4h8"/>
              </svg>
              Egyedi feltöltés
            </button>
            <router-link 
              :to="'/children/' + childId + '/billing'"
              class="modern-btn modern-btn--secondary btn-sm"
              title="Számlázási és egyenlegkezelési felület megnyitása"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="5" width="20" height="14" rx="2"/>
                <line x1="2" y1="10" x2="22" y2="10"/>
              </svg>
              Számlázás
            </router-link>
          </div>
        </div>
      </div>

      <!-- Díjcsoportok (Fee groups / billing plan) -->
      <div class="info-item" v-if="isAdmin">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          </svg>
          Díjcsoportok
        </div>
        <div class="info-value">
          <div class="fee-groups-display">
            <ul class="fee-list">
              <li v-for="fee in feeGroups" :key="fee.id">
                <span>{{ fee.group_name }} — {{ fee.billing_mode == 'monthly' ? 'Havi (fix ár)' : 'Óránkénti' }}</span>
                <button class="modern-btn modern-btn--danger btn-sm" @click="$emit('remove-fee-group', fee.id)">Törlés</button>
              </li>
              <li v-if="!feeGroups || feeGroups.length === 0" class="muted">Nincs beállított díjcsoport.</li>
            </ul>
            <div class="finance-row fee-form">
              <select class="modern-input small" v-model.number="localFeeForm.group_id">
                <option :value="0" disabled>Csoport...</option>
                <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
              </select>
              <select class="modern-input small" v-model="localFeeForm.billing_mode">
                <option value="monthly">Havi (fix ár)</option>
                <option value="class">Óránként</option>
              </select>
              <button class="modern-btn modern-btn--primary btn-sm" @click="$emit('add-fee-group', localFeeForm)">Hozzáadás</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Parent and Dancer View: No payment management information, only see if paid or still owe money and how much -->
  <div class="info-card" v-else>
    <div class="card-header">
      <div class="card-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      </div>
      <h3>Pénzügyi státusz</h3>
    </div>

    <div class="info-list">
      <div class="info-item">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Fizetési állapot
        </div>
        <div class="info-value">
          <div v-if="balance < 0" class="d-flex flex-column gap-1">
            <span class="badge bg-danger-subtle text-danger border border-danger fs-6 px-3 py-1.5 rounded-pill font-weight-bold d-inline-flex align-items-center gap-1.5" style="width: fit-content;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Fennálló tartozás
            </span>
            <div class="fs-6 mt-1">
              Tartozás összege: <strong class="text-danger fs-5">{{ formatHuf(Math.abs(balance)) }}</strong>
            </div>
            <p class="text-muted small mb-0 mt-1">
              Kérjük rendezze a fennálló tartozást.
            </p>
          </div>
          <div v-else class="d-flex flex-column gap-1">
            <span class="badge bg-success-subtle text-success border border-success fs-6 px-3 py-1.5 rounded-pill font-weight-bold d-inline-flex align-items-center gap-1.5" style="width: fit-content;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Minden fizetve
            </span>
            <div class="fs-6 mt-1 text-muted">
              Fennálló tartozás: <strong class="text-success">0 Ft</strong>
            </div>
            <p class="text-muted small mb-0 mt-1">
              Minden szükséges tagdíj és befizetés rendezve van.
            </p>
          </div>
        </div>
      </div>
      <div class="info-item">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <line x1="2" y1="10" x2="22" y2="10"/>
          </svg>
          Részletek
        </div>
        <div class="info-value">
          <router-link :to="'/children/' + childId + '/billing'" class="modern-btn modern-btn--primary btn-sm">
            Számlázási oldal megtekintése &rarr;
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChildFinanceCard',
  props: {
    childId: {
      type: [String, Number],
      default: ''
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    isTeacher: {
      type: Boolean,
      default: false
    },
    isAssistant: {
      type: Boolean,
      default: false
    },
    balance: {
      type: Number,
      default: 0
    },
    monthlyFee: {
      type: Number,
      default: 0
    },
    monthlyFeePaid: {
      type: Boolean,
      default: false
    },
    currentMonth: {
      type: String,
      default: ''
    },
    feeGroups: {
      type: Array,
      default: () => []
    },
    groups: {
      type: Array,
      default: () => []
    },
    feeForm: {
      type: Object,
      default: () => ({ billing_mode: 'monthly', group_id: 0 })
    },
    birthYear: {
      type: [Number, String],
      default: null
    },
    birthMonth: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['save-birth', 'top-up-monthly', 'open-custom-topup', 'add-fee-group', 'remove-fee-group'],
  data() {
    return {
      localBirthYear: this.birthYear,
      localBirthMonth: this.birthMonth,
      localFeeForm: { ...this.feeForm }
    };
  },
  watch: {
    birthYear(newVal) {
      this.localBirthYear = newVal;
    },
    birthMonth(newVal) {
      this.localBirthMonth = newVal;
    },
    feeForm: {
      deep: true,
      handler(newVal) {
        this.localFeeForm = { ...newVal };
      }
    }
  },
  methods: {
    formatHuf(v) {
      return (v || 0).toLocaleString('hu-HU') + ' HUF';
    }
  }
};
</script>

<style scoped>
.info-card {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  overflow: hidden;
  border: 1px solid var(--color-border);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: var(--color-background-soft);
  border-bottom: 1px solid var(--color-border);
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: var(--button-primary, #2563eb);
  border-radius: 12px;
  color: white;
}

.card-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
}

.info-list {
  padding: 1.5rem 2rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--color-border);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  font-weight: 600;
  color: var(--color-text);
}

.info-label svg {
  color: var(--button-primary, #2563eb);
  flex-shrink: 0;
}

.info-value {
  flex: 1;
  color: var(--color-text-muted);
  word-break: break-word;
}

.finance-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.modern-input.small {
  max-width: 140px;
  padding: 0.35rem 0.6rem;
  font-size: 0.875rem;
}

.payment-info-value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.current-balance-display {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.payment-buttons-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.fee-groups-display {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.fee-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.fee-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-background-soft);
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--color-border);
}

.fee-list li.muted {
  color: var(--color-text-muted);
  font-style: italic;
  background: transparent;
  border: none;
}

.fee-form {
  margin-top: 0.5rem;
}
</style>
